/**
 * Component: SearchFilterPanel (widget)
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R27
 *
 * Compact, collapsible faceted-filter panel for the widget search surfaces.
 * Functionally mirrors the web app's SearchFilterSidebar (capability/facet-driven
 * controls, COR-class coupling, date presets, recommendations-only + recency
 * toggles, figures-and-tables scope, Clear all) but is built widget-native:
 * native HTML controls styled inline with the `--galen-*` CSS variables, so it
 * carries no shadcn/Tailwind dependency and themes with the rest of the widget.
 */

import React, { useEffect, useState } from 'react';
import {
  type Facet,
  type SearchFilters,
  ANY_TIME_PRESET_VALUE,
  DATE_PRESETS,
  RECOMMENDATION_FACET_FIELD,
  hasActiveFilters,
  presetRange,
} from './search-filter-types';

export interface SearchFilterPanelProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onReset: () => void;
  facets: Facet[];
  resultCount?: number;
  /** Simple-search only: expose the figures-and-tables scope toggle. */
  showFiguresToggle?: boolean;
  /**
   * When this value changes the panel collapses. Surfaces bump it on a fresh
   * query submit (not on a filter-driven re-search) so a new result set closes
   * the refine panel without closing it mid-filtering.
   */
  collapseToken?: number;
}

const TYPEAHEAD_THRESHOLD = 8;

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--galen-border, #e2e8f0)',
  borderRadius: 'var(--galen-border-radius, 0.5rem)',
  background: 'var(--galen-card, #ffffff)',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
};

const headerButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: 'none',
  background: 'transparent',
  color: 'var(--galen-foreground, #0f172a)',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const bodyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0 0.75rem 0.75rem',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--galen-muted-foreground, #64748b)',
  marginBottom: '0.25rem',
};

const checkRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  cursor: 'pointer',
  lineHeight: 1.4,
};

const controlStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.3rem 0.4rem',
  border: '1px solid var(--galen-border, #e2e8f0)',
  borderRadius: 'var(--galen-border-radius, 0.375rem)',
  background: 'var(--galen-background, #ffffff)',
  color: 'var(--galen-foreground, #0f172a)',
  fontFamily: 'inherit',
  fontSize: '0.8rem',
  boxSizing: 'border-box',
};

const clearButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--galen-primary, #0f172a)',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
};

function ToggleRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): React.ReactElement {
  return (
    <label htmlFor={id} style={checkRowStyle}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/** A single facet field rendered as a checkbox list, with a filter input for high-cardinality fields. */
function FacetGroup({
  facet,
  selected,
  onToggle,
}: {
  facet: Facet;
  selected: string[];
  onToggle: (value: string) => void;
}): React.ReactElement {
  const [filterText, setFilterText] = useState('');
  const isTypeahead = facet.values.length > TYPEAHEAD_THRESHOLD;
  const needle = filterText.trim().toLowerCase();
  const visible = isTypeahead
    ? facet.values.filter(
        (v) => selected.includes(v.value) || v.value.toLowerCase().includes(needle),
      )
    : facet.values;

  return (
    <div>
      <div style={sectionLabelStyle}>{facet.label}</div>
      {isTypeahead && (
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={`Filter ${facet.label.toLowerCase()}...`}
          aria-label={`Filter ${facet.label}`}
          style={{ ...controlStyle, marginBottom: '0.35rem' }}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '10rem', overflowY: 'auto' }}>
        {visible.map((v) => {
          const id = `facet-${facet.field}-${v.value}`;
          return (
            <label key={v.value} htmlFor={id} style={checkRowStyle}>
              <input
                id={id}
                type="checkbox"
                aria-label={v.value}
                checked={selected.includes(v.value)}
                onChange={() => onToggle(v.value)}
              />
              <span style={{ flex: 1 }}>{v.value}</span>
              <span style={{ color: 'var(--galen-muted-foreground, #64748b)' }}>{v.count}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function SearchFilterPanel({
  filters,
  onFiltersChange,
  onReset,
  facets,
  resultCount,
  showFiguresToggle = false,
  collapseToken,
}: SearchFilterPanelProps): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  // Collapse the panel when a fresh search runs (the surface bumps collapseToken
  // on a new query submit, not on a filter-driven re-search).
  useEffect(() => setOpen(false), [collapseToken]);
  // Date inputs are buffered and committed on blur so navigating the native
  // date picker does not re-run the search with an intermediate value.
  const [dateFromInput, setDateFromInput] = useState(filters.dateFrom);
  const [dateToInput, setDateToInput] = useState(filters.dateTo);
  useEffect(() => setDateFromInput(filters.dateFrom), [filters.dateFrom]);
  useEffect(() => setDateToInput(filters.dateTo), [filters.dateTo]);

  const recFacet = facets.find((f) => f.field === RECOMMENDATION_FACET_FIELD);
  const hasRecommendations =
    (recFacet?.values.length ?? 0) > 0 || filters.recommendationsOnly;

  // Capability-driven: show a facet when it has more than one value, or it has
  // an active selection (so a narrowed-to-one facet stays visible).
  const visibleFacets = facets.filter(
    (f) =>
      f.values.length > 1 || (filters.facetSelections[f.field]?.length ?? 0) > 0,
  );

  const active = hasActiveFilters(filters);

  const commitDate = (key: 'dateFrom' | 'dateTo', value: string): void => {
    if (value !== filters[key]) onFiltersChange({ ...filters, [key]: value });
  };

  const toggleFacetValue = (field: string, value: string): void => {
    const current = filters.facetSelections[field] ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const facetSelections = { ...filters.facetSelections, [field]: next };
    if (next.length === 0) delete facetSelections[field];
    // COR-class coupling: selecting any recommendation class implies
    // recommendations-only scope.
    const recommendationsOnly =
      field === RECOMMENDATION_FACET_FIELD
        ? next.length > 0 || filters.recommendationsOnly
        : filters.recommendationsOnly;
    onFiltersChange({ ...filters, facetSelections, recommendationsOnly });
  };

  const setRecommendationsOnly = (checked: boolean): void => {
    if (!checked) {
      // Unchecking clears any selected COR classes.
      const facetSelections = { ...filters.facetSelections };
      delete facetSelections[RECOMMENDATION_FACET_FIELD];
      onFiltersChange({ ...filters, recommendationsOnly: false, facetSelections });
    } else {
      onFiltersChange({ ...filters, recommendationsOnly: true });
    }
  };

  const applyPreset = (value: string): void => {
    const range = presetRange(value);
    if (range) onFiltersChange({ ...filters, dateFrom: range.from, dateTo: range.to });
  };

  // Nothing to refine on a corpus that returns no facets and no toggles apply.
  if (visibleFacets.length === 0 && !hasRecommendations && !showFiguresToggle && !active) {
    return null;
  }

  return (
    <div style={panelStyle}>
      <button
        type="button"
        style={headerButtonStyle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="widget-filter-toggle"
      >
        <span>
          Refine
          {resultCount !== undefined
            ? ` · ${resultCount} result${resultCount === 1 ? '' : 's'}`
            : ''}
          {active ? ' •' : ''}
        </span>
        <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div style={bodyStyle} data-testid="widget-filter-body">
          {active && (
            <button type="button" style={clearButtonStyle} onClick={onReset} data-testid="widget-filter-clear">
              Clear all
            </button>
          )}

          {hasRecommendations && (
            <ToggleRow
              id="widget-filter-recommendations"
              label="Recommendations only"
              checked={filters.recommendationsOnly}
              onChange={setRecommendationsOnly}
            />
          )}

          <ToggleRow
            id="widget-filter-recent"
            label="Prioritise recent publications"
            checked={filters.prioritizeRecent}
            onChange={(checked) => onFiltersChange({ ...filters, prioritizeRecent: checked })}
          />

          {showFiguresToggle && (
            <ToggleRow
              id="widget-filter-figures"
              label="Figures and tables only"
              checked={filters.limitToFiguresAndTables}
              onChange={(checked) =>
                onFiltersChange({ ...filters, limitToFiguresAndTables: checked })
              }
            />
          )}

          {/* Publication date */}
          <div>
            <div style={sectionLabelStyle}>Publication date</div>
            <select
              aria-label="Quick date range"
              style={{ ...controlStyle, marginBottom: '0.35rem' }}
              value=""
              onChange={(e) => {
                if (e.target.value) applyPreset(e.target.value);
              }}
            >
              <option value="">Quick range...</option>
              <option value={ANY_TIME_PRESET_VALUE}>Any time</option>
              {DATE_PRESETS.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input
                type="date"
                aria-label="From date"
                value={dateFromInput}
                onChange={(e) => setDateFromInput(e.target.value)}
                onBlur={() => commitDate('dateFrom', dateFromInput)}
                style={controlStyle}
              />
              <input
                type="date"
                aria-label="To date"
                value={dateToInput}
                onChange={(e) => setDateToInput(e.target.value)}
                onBlur={() => commitDate('dateTo', dateToInput)}
                style={controlStyle}
              />
            </div>
          </div>

          {/* Facet groups */}
          {visibleFacets.map((facet) => (
            <FacetGroup
              key={facet.field}
              facet={facet}
              selected={filters.facetSelections[facet.field] ?? []}
              onToggle={(value) => toggleFacetValue(facet.field, value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
